import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { Play, Pause, SkipForward, SkipBack, Trash2, Plus, Music2, Volume2, GripVertical, ChevronDown, Pencil, Check, X } from "lucide-react";
import { useAdmin } from "../contexts/AdminContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Persistent audio player bar (used for P7 in-studio playlist).
 * Lives as a docked bottom bar.
 */
export default function AudioPlayer({ section, isNight, onUpdated, onCollapsedChange }) {
    const accent = isNight ? "#D4AF37" : "#B89B5E";
    const { isAdmin } = useAdmin();
    // Re-derive tracks whenever the section reference changes so React
    // reliably re-renders the list after add/delete.
    const tracks = React.useMemo(
        () => (section?.media?.audio || []).slice(),
        [section, section?.media?.audio],
    );
    const [idx, setIdx] = useState(0);
    const [playing, setPlaying] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [collapsed, setCollapsed] = useState(false);
    const audioRef = useRef(null);
    const inputRef = useRef(null);

    const current = tracks[idx];

    // Sync collapsed state whenever the section changes (each section has its own key).
    useEffect(() => {
        try {
            const v = sessionStorage.getItem(`audio_collapsed_${section?.id}`) === "1";
            setCollapsed(v);
        } catch (_e) { setCollapsed(false); }
    }, [section?.id]);

    const setCollapsedPersisted = (v) => {
        setCollapsed(v);
        try { sessionStorage.setItem(`audio_collapsed_${section?.id}`, v ? "1" : "0"); } catch (_e) { /* noop */ }
        if (onCollapsedChange) onCollapsedChange(v);
    };

    // Notify parent of the initial collapsed state (from sessionStorage) + whenever section changes.
    useEffect(() => {
        if (onCollapsedChange) onCollapsedChange(collapsed);
    }, [section?.id, collapsed, onCollapsedChange]);

    useEffect(() => {
        if (!audioRef.current) return;
        if (playing) {
            audioRef.current.play().catch(() => setPlaying(false));
        } else {
            audioRef.current.pause();
        }
    }, [playing, idx]);

    // Keep idx in range whenever the tracks length changes (post add/delete).
    useEffect(() => {
        if (tracks.length === 0) {
            if (idx !== 0) setIdx(0);
            return;
        }
        if (idx >= tracks.length) setIdx(0);
    }, [tracks.length, idx]);

    const togglePlay = () => {
        if (!current) return;
        setPlaying((p) => !p);
    };

    const next = () => {
        if (tracks.length === 0) return;
        setIdx((i) => (i + 1) % tracks.length);
        setPlaying(true);
    };

    const prev = () => {
        if (tracks.length === 0) return;
        setIdx((i) => (i - 1 + tracks.length) % tracks.length);
        setPlaying(true);
    };

    const onPickFiles = async (files) => {
        if (!files || files.length === 0) return;
        setUploading(true);
        try {
            let updated = section;
            for (const f of Array.from(files)) {
                const fd = new FormData();
                fd.append("file", f);
                fd.append("kind", "audio");
                fd.append("title", f.name.replace(/\.[^.]+$/, ""));
                const res = await axios.post(`${API}/sections/${section.id}/media`, fd);
                updated = res.data;
            }
            onUpdated && onUpdated(updated);
        } catch (e) {
            console.error("Audio upload failed", e);
        } finally {
            setUploading(false);
            if (inputRef.current) inputRef.current.value = "";
        }
    };

    const onDelete = async (deleteIdx) => {
        try {
            const res = await axios.delete(`${API}/sections/${section.id}/media`, {
                params: { kind: "audio", index: deleteIdx },
            });
            onUpdated && onUpdated(res.data);
            if (deleteIdx === idx) setPlaying(false);
        } catch (e) { console.error(e); }
    };

    // ---- Inline rename (admin only) ----
    const [editIdx, setEditIdx] = useState(null);
    const [editValue, setEditValue] = useState("");
    const [renaming, setRenaming] = useState(false);

    const startEdit = (e, i) => {
        e.stopPropagation();
        setEditIdx(i);
        setEditValue(tracks[i]?.title || "");
    };
    const cancelEdit = (e) => {
        if (e) e.stopPropagation();
        setEditIdx(null);
        setEditValue("");
    };
    const commitEdit = async (e) => {
        if (e) e.stopPropagation();
        const newTitle = (editValue || "").trim();
        if (editIdx === null || !newTitle || newTitle === tracks[editIdx]?.title) {
            cancelEdit();
            return;
        }
        setRenaming(true);
        try {
            const res = await axios.patch(`${API}/sections/${section.id}/media/rename`, {
                kind: "audio",
                index: editIdx,
                title: newTitle,
            });
            onUpdated && onUpdated(res.data);
        } catch (err) {
            console.error("Rename failed", err);
        } finally {
            setRenaming(false);
            setEditIdx(null);
            setEditValue("");
        }
    };

    // ---- Drag & drop reordering (admin only) ----
    const [dragIdx, setDragIdx] = useState(null);
    const [overIdx, setOverIdx] = useState(null);

    const onDragStart = (e, i) => {
        if (!isAdmin) return;
        setDragIdx(i);
        try { e.dataTransfer.effectAllowed = "move"; } catch (_err) { /* noop */ }
    };
    const onDragOver = (e, i) => {
        if (!isAdmin || dragIdx === null) return;
        e.preventDefault();
        try { e.dataTransfer.dropEffect = "move"; } catch (_err) { /* noop */ }
        if (overIdx !== i) setOverIdx(i);
    };
    const onDragEnd = () => { setDragIdx(null); setOverIdx(null); };
    const onDrop = async (e, to) => {
        if (!isAdmin) return;
        e.preventDefault();
        const from = dragIdx;
        setDragIdx(null); setOverIdx(null);
        if (from === null || from === to) return;
        try {
            const res = await axios.patch(`${API}/sections/${section.id}/media/reorder`, {
                kind: "audio",
                from_index: from,
                to_index: to,
            });
            onUpdated && onUpdated(res.data);
            // Keep the currently-playing track selected after reorder.
            setIdx((cur) => {
                if (cur === from) return to;
                if (from < cur && cur <= to) return cur - 1;
                if (to <= cur && cur < from) return cur + 1;
                return cur;
            });
        } catch (err) { console.error(err); }
    };

    return collapsed ? (
        <button
            type="button"
            data-testid={`audio-expand-${section.id}`}
            onClick={() => setCollapsedPersisted(false)}
            className="absolute z-30 bottom-3 md:bottom-6 right-3 md:right-6 rounded-full grid place-items-center transition-all hover:scale-105"
            style={{
                width: 44,
                height: 44,
                background: accent,
                color: "#0a0a0a",
                boxShadow: `0 8px 22px -8px ${accent}`,
                border: `1px solid ${accent}`,
            }}
            title="Show music player"
        >
            <Music2 size={18} />
        </button>
    ) : (
        <div
            data-testid={`audio-player-${section.id}`}
            className="absolute z-30 bottom-3 md:bottom-6 left-1/2 -translate-x-1/2 w-[94%] sm:w-[92%] max-w-3xl"
        >
            <div
                className={`${isNight ? "glass" : "glass-day"} rounded-2xl p-2.5 md:p-4 relative`}
                style={{ border: `1px solid ${accent}55` }}
            >
                {/* Collapse chevron — always accessible */}
                <button
                    type="button"
                    data-testid={`audio-collapse-${section.id}`}
                    onClick={() => setCollapsedPersisted(true)}
                    className="absolute -top-3 right-3 w-7 h-7 rounded-full grid place-items-center transition-all hover:scale-105"
                    style={{
                        background: isNight ? "rgba(20,20,26,0.98)" : "rgba(250,246,235,0.98)",
                        color: accent,
                        border: `1px solid ${accent}80`,
                        boxShadow: `0 4px 14px -6px ${accent}80`,
                    }}
                    title="Hide music player"
                >
                    <ChevronDown size={13} />
                </button>
                <div className="flex items-center gap-3 md:gap-4">
                    <div
                        className="w-11 h-11 rounded-full grid place-items-center shrink-0"
                        style={{ background: `${accent}15`, border: `1px solid ${accent}55`, color: accent }}
                    >
                        <Music2 size={16} />
                    </div>

                    <div className="flex-1 min-w-0">
                        <div
                            className="text-[9px] tracking-[0.35em] uppercase opacity-60"
                            style={{ color: isNight ? "#fff" : "#0a0a0a" }}
                        >
                            In-Studio Playlist
                        </div>
                        <div
                            data-testid={`audio-now-playing-${section.id}`}
                            className="text-sm truncate font-medium"
                            style={{ color: isNight ? "#fff" : "#0a0a0a" }}
                        >
                            {current ? current.title : "Upload tracks to fill the studio"}
                        </div>
                    </div>

                    <div className="flex items-center gap-1 shrink-0">
                        <button
                            type="button"
                            onClick={prev}
                            disabled={tracks.length === 0}
                            data-testid={`audio-prev-${section.id}`}
                            className="w-9 h-9 rounded-full grid place-items-center disabled:opacity-30"
                            style={{ background: "transparent", color: isNight ? "#fff" : "#0a0a0a" }}
                        >
                            <SkipBack size={16} />
                        </button>
                        <button
                            type="button"
                            onClick={togglePlay}
                            disabled={tracks.length === 0}
                            data-testid={`audio-play-${section.id}`}
                            className="w-11 h-11 rounded-full grid place-items-center transition-all hover:scale-105 disabled:opacity-30"
                            style={{ background: accent, color: "#0a0a0a", boxShadow: `0 8px 22px -10px ${accent}` }}
                        >
                            {playing ? <Pause size={16} /> : <Play size={16} />}
                        </button>
                        <button
                            type="button"
                            onClick={next}
                            disabled={tracks.length === 0}
                            data-testid={`audio-next-${section.id}`}
                            className="w-9 h-9 rounded-full grid place-items-center disabled:opacity-30"
                            style={{ background: "transparent", color: isNight ? "#fff" : "#0a0a0a" }}
                        >
                            <SkipForward size={16} />
                        </button>
                        <button
                            type="button"
                            onClick={() => inputRef.current?.click()}
                            data-testid={`audio-upload-${section.id}`}
                            className="ml-1 w-9 h-9 rounded-full grid place-items-center"
                            style={{
                                background: `${accent}18`,
                                color: accent,
                                border: `1px solid ${accent}55`,
                                visibility: isAdmin ? "visible" : "hidden",
                            }}
                            title="Add tracks"
                        >
                            <Plus size={14} />
                        </button>
                        <input
                            ref={inputRef}
                            type="file"
                            accept="audio/mpeg,audio/mp3,audio/wav,audio/ogg,audio/x-m4a,audio/aac,audio/flac"
                            multiple
                            className="hidden"
                            data-testid={`audio-file-input-${section.id}`}
                            onChange={(e) => onPickFiles(e.target.files)}
                        />
                    </div>
                </div>

                {/* Track list (only if more than one) */}
                {tracks.length > 0 && (
                    <div
                        className="mt-3 max-h-32 overflow-y-auto rounded-lg"
                        data-testid={`audio-track-list-${section.id}`}
                    >
                        {tracks.map((t, i) => (
                            <div
                                key={t.url || `track-${i}`}
                                draggable={isAdmin}
                                onDragStart={(e) => onDragStart(e, i)}
                                onDragOver={(e) => onDragOver(e, i)}
                                onDragEnd={onDragEnd}
                                onDrop={(e) => onDrop(e, i)}
                                className={`flex items-center gap-2 px-2 py-1.5 text-xs rounded-lg cursor-pointer ${i === idx ? "opacity-100" : "opacity-65 hover:opacity-90"} ${dragIdx === i ? "opacity-40" : ""} ${overIdx === i && dragIdx !== null && dragIdx !== i ? "ring-1" : ""}`}
                                style={{
                                    color: i === idx ? accent : (isNight ? "#fff" : "#0a0a0a"),
                                    background: i === idx ? `${accent}10` : "transparent",
                                    boxShadow: overIdx === i && dragIdx !== null && dragIdx !== i ? `inset 0 1px 0 ${accent}, inset 0 -1px 0 ${accent}` : "none",
                                }}
                                onClick={() => { setIdx(i); setPlaying(true); }}
                                data-testid={`audio-track-${section.id}-${i}`}
                            >
                                <span
                                    data-testid={`audio-drag-${section.id}-${i}`}
                                    className="shrink-0"
                                    style={{
                                        cursor: isAdmin ? "grab" : "default",
                                        color: isNight ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.45)",
                                        visibility: isAdmin ? "visible" : "hidden",
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <GripVertical size={12} />
                                </span>
                                <Volume2 size={12} style={{ visibility: i === idx && playing ? "visible" : "hidden" }} />
                                {editIdx === i ? (
                                    <>
                                        <input
                                            autoFocus
                                            type="text"
                                            value={editValue}
                                            onChange={(e) => setEditValue(e.target.value)}
                                            onClick={(e) => e.stopPropagation()}
                                            onKeyDown={(e) => {
                                                e.stopPropagation();
                                                if (e.key === "Enter") commitEdit(e);
                                                else if (e.key === "Escape") cancelEdit(e);
                                            }}
                                            disabled={renaming}
                                            data-testid={`audio-rename-input-${section.id}-${i}`}
                                            className="flex-1 min-w-0 bg-transparent outline-none border-b text-xs py-0.5"
                                            style={{
                                                color: isNight ? "#fff" : "#0a0a0a",
                                                borderColor: `${accent}80`,
                                            }}
                                        />
                                        <button
                                            type="button"
                                            onClick={commitEdit}
                                            disabled={renaming}
                                            data-testid={`audio-rename-save-${section.id}-${i}`}
                                            style={{ color: accent }}
                                            title="Save"
                                        >
                                            <Check size={12} />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={cancelEdit}
                                            disabled={renaming}
                                            data-testid={`audio-rename-cancel-${section.id}-${i}`}
                                            style={{ color: isNight ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.5)" }}
                                            title="Cancel"
                                        >
                                            <X size={12} />
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <span className="flex-1 truncate">{t.title}</span>
                                        <button
                                            type="button"
                                            onClick={(e) => startEdit(e, i)}
                                            data-testid={`audio-rename-${section.id}-${i}`}
                                            style={{
                                                color: isNight ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.5)",
                                                visibility: isAdmin ? "visible" : "hidden",
                                            }}
                                            title="Rename track"
                                        >
                                            <Pencil size={11} />
                                        </button>
                                    </>
                                )}
                                <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); onDelete(i); }}
                                    data-testid={`audio-delete-${section.id}-${i}`}
                                    style={{
                                        color: isNight ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.5)",
                                        visibility: isAdmin ? "visible" : "hidden",
                                    }}
                                >
                                    <Trash2 size={11} />
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Hidden audio element */}
            <audio
                ref={audioRef}
                src={current?.url}
                onEnded={next}
                onPause={() => setPlaying(false)}
                onPlay={() => setPlaying(true)}
                data-testid={`audio-element-${section.id}`}
            />

            {uploading && (
                <div
                    className="mt-2 text-center text-[9px] tracking-[0.3em] uppercase"
                    style={{ color: accent }}
                >
                    Uploading…
                </div>
            )}
        </div>
    );
}
